<template>
  <div class="asset-image-container">
    <!-- Image Display -->
    <div class="relative">
      <div 
        class="asset-image"
        :class="[
          { 
            'loading': isLoading, 
            'error': hasError,
            'no-image': !effectiveImageUrl && !isLoading
          },
          `size-${props.size}`
        ]"
        @click="handleImageClick"
      >
        <img 
          v-if="effectiveImageUrl && !hasError" 
          :src="effectiveImageUrl" 
          :alt="imageAlt"
          class="w-full h-full object-cover rounded-full"
          @load="handleImageLoad"
          @error="handleImageError"
        >
        <div
          v-else-if="isLoading"
          class="flex items-center justify-center"
        >
          <i class="pi pi-spin pi-spinner text-2xl text-gray-400" />
        </div>
        <div
          v-else
          class="flex items-center justify-center"
        >
          <i class="pi pi-image text-4xl text-gray-400" />
        </div>
      </div>
            
      <!-- Image Source Badge -->
      <div 
        v-if="showSourceBadge" 
        class="asset-image-source-badge"
        :class="sourceBadgeClass"
      >
        {{ sourceBadgeText }}
      </div>
            
      <!-- Upload Progress -->
      <div
        v-if="uploadProgress > 0 && uploadProgress < 100"
        class="upload-progress"
      >
        <div
          class="upload-progress-bar"
          :style="{ width: uploadProgress + '%' }"
        />
        <span class="upload-progress-text">{{ uploadProgress }}%</span>
      </div>
            
      <!-- Image Validation Recommendations -->
      <div
        v-if="imageValidation && imageValidation.recommendations && imageValidation.recommendations.length > 0" 
        class="image-recommendations"
      >
        <div class="recommendation-header">
          <i class="pi pi-info-circle text-blue-500" />
          <span class="text-sm font-medium">Image Recommendations</span>
        </div>
        <ul class="recommendation-list">
          <li
            v-for="recommendation in imageValidation.recommendations" 
            :key="recommendation" 
            class="recommendation-item"
          >
            {{ recommendation }}
          </li>
        </ul>
      </div>
    </div>
        
    <!-- Image Actions (when in edit mode) -->
    <div
      v-if="isEdit && showActions"
      class="image-actions mt-2"
    >
      <Button 
        icon="pi pi-upload" 
        label="Upload" 
        size="small" 
        severity="secondary" 
        :disabled="isUploading"
        @click="triggerUpload"
      />
      <Button 
        v-if="hasLocalImage"
        icon="pi pi-trash" 
        label="Delete" 
        size="small" 
        severity="danger" 
        :disabled="isDeleting"
        @click="confirmDelete"
      />
      <Button 
        v-if="hasSubAssetImage && !hasLocalImage"
        icon="pi pi-eye" 
        label="View Shared" 
        size="small" 
        severity="info" 
        @click="viewSharedImage"
      />
    </div>
        
    <!-- Hidden File Input -->
    <input 
      ref="fileInput"
      type="file" 
      accept="image/*" 
      class="hidden"
      @change="handleFileSelect"
    >
        
    <!-- Image Suggestion Modal -->
    <ImageSuggestionModal
      v-model:visible="isSuggestionModalVisible"
      :suggestions="suggestions"
      :is-loading="isLoadingSuggestions"
      :asset-id="props.data?.id"
      @close="handleSuggestionModalClose"
      @use-selected="handleUseSuggestion"
      @upload-new="handleUploadNew"
    />
        
    <!-- Image Dialog -->
    <Dialog 
      v-model:visible="isImageDialogVisible" 
      :header="imageDialogHeader"
      :modal="true" 
      :closable="true"
      :style="{ width: 'auto', maxWidth: '90vw' }"
    >
      <div class="image-dialog-content">
        <img 
          :src="dialogImageUrl" 
          :alt="imageAlt"
          class="max-w-full max-h-[80vh] object-contain"
        >
                
        <!-- Image Information -->
        <div class="mt-4 space-y-2">
          <div
            v-if="imageDescription"
            class="text-sm text-gray-600"
          >
            <strong>Description:</strong> {{ imageDescription }}
          </div>
                    
          <div
            v-if="imageValidation"
            class="text-sm text-gray-600"
          >
            <strong>Dimensions:</strong> {{ imageValidation.width }}x{{ imageValidation.height }}
            <span
              v-if="imageValidation.aspect_ratio"
              class="ml-2"
            >
              ({{ imageValidation.aspect_ratio.toFixed(2) }}:1)
            </span>
          </div>
                    
          <div
            v-if="imageSource"
            class="text-sm text-gray-600"
          >
            <strong>Source:</strong> {{ imageDescription || imageDialogHeader }}
          </div>
        </div>
                
        <!-- Available Sizes -->
        <div
          v-if="Object.keys(getAllImageSizes()).length > 0"
          class="mt-4"
        >
          <h4 class="text-sm font-medium text-gray-700 mb-2">
            Available Sizes:
          </h4>
          <div class="grid grid-cols-2 gap-2">
            <div
              v-for="(sizeData, size) in getAllImageSizes()" 
              :key="size"
              class="text-xs text-gray-500 p-2 bg-gray-50 rounded"
            >
              <div class="font-medium">
                {{ size.charAt(0).toUpperCase() + size.slice(1) }}
              </div>
              <div v-if="sizeData.width && sizeData.height">
                {{ sizeData.width }}x{{ sizeData.height }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
        
    <!-- Delete Confirmation Dialog -->
    <Dialog 
      v-model:visible="isDeleteDialogVisible" 
      header="Delete Image" 
      :modal="true"
      :style="{ width: '400px' }"
    >
      <div class="delete-confirmation">
        <p>Are you sure you want to delete this image?</p>
        <p class="text-sm text-gray-600 mt-2">
          {{ hasLocalImage ? 'This will remove your company-specific image and fall back to the shared image.' : 'This will remove the shared image.' }}
        </p>
      </div>
      <template #footer>
        <Button 
          label="Cancel" 
          icon="pi pi-times" 
          severity="secondary" 
          text 
          @click="isDeleteDialogVisible = false" 
        />
        <Button 
          label="Delete" 
          icon="pi pi-trash" 
          severity="danger" 
          :loading="isDeleting" 
          @click="deleteImage"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { LocalAssetService } from '@/services/localAssetService';
import ImageSuggestionModal from './ImageSuggestionModal.vue';
import { ImageSuggestionService } from '@/services/imageSuggestionService';

const props = defineProps({
    data: {
        type: Object,
        required: true
    },
    isEdit: {
        type: Boolean,
        default: false
    },
    showActions: {
        type: Boolean,
        default: true
    },
    showSourceBadge: {
        type: Boolean,
        default: true
    },
    size: {
        type: String,
        default: 'medium', // thumbnail, small, medium, large, original
        validator: (value) => ['thumbnail', 'small', 'medium', 'large', 'original'].includes(value)
    },
    clickable: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['image-updated', 'image-uploaded', 'image-deleted']);

// Reactive state
const isLoading = ref(false);
const hasError = ref(false);
const isUploading = ref(false);
const isDeleting = ref(false);
const uploadProgress = ref(0);
const isImageDialogVisible = ref(false);
const isDeleteDialogVisible = ref(false);
const dialogImageUrl = ref('');
const fileInput = ref(null);
const imageValidation = ref(null);

// Image suggestion state
const isSuggestionModalVisible = ref(false);
const suggestions = ref([]);
const isLoadingSuggestions = ref(false);

// Computed properties
const effectiveImageUrl = computed(() => {
    if (!props.data) return null;
    
    // Use the effective image URL from the API response
    const baseUrl = props.data.image_url;
    if (!baseUrl) return null;
    
    // If we have image_sizes data from upload response, use that
    if (props.data.image_sizes && props.data.image_sizes[props.size]) {
        return props.data.image_sizes[props.size].url;
    }
    
    // Fallback to URL manipulation for existing images
    return getImageSizeUrl(baseUrl, props.size);
});

const imageSource = computed(() => {
    return props.data?.image_source || null;
});

const imageDescription = computed(() => {
    return props.data?.image_description || null;
});

const imageAlt = computed(() => {
    return props.data?.display_name || props.data?.name || 'Asset Image';
});

const hasLocalImage = computed(() => {
    return imageSource.value === 'local_asset';
});

const hasClassificationDefaultImage = computed(() => {
    return ['department_default', 'master_category_default', 'category_default'].includes(imageSource.value);
});

const hasSubAssetImage = computed(() => {
    return props.data?.sub_asset_data?.image_url && imageSource.value !== 'local_asset';
});

const sourceBadgeClass = computed(() => {
    if (hasLocalImage.value) return 'company-image-badge';
    if (hasClassificationDefaultImage.value) return 'company-image-badge';
    if (imageSource.value === 'sub_asset') return 'shared-image-badge';
    return 'no-image-badge';
});

const sourceBadgeText = computed(() => {
    if (hasLocalImage.value) return 'Company';
    if (imageSource.value === 'department_default') return 'Dept';
    if (imageSource.value === 'master_category_default') return 'Master Cat';
    if (imageSource.value === 'category_default') return 'Category';
    if (imageSource.value === 'sub_asset') return 'Shared';
    return 'No Image';
});

const imageDialogHeader = computed(() => {
    if (hasLocalImage.value) return 'Company Image';
    if (imageSource.value === 'department_default') return 'Department Default Image';
    if (imageSource.value === 'master_category_default') return 'Master Category Default Image';
    if (imageSource.value === 'category_default') return 'Category Default Image';
    if (imageSource.value === 'sub_asset') return 'Shared Image';
    return 'Asset Image';
});

// Methods
const getImageSizeUrl = (baseUrl, size) => {
    if (!baseUrl) return null;
    
    // Replace the size in the URL
    const sizeMap = {
        thumbnail: '-thumbnail-',
        small: '-small-',
        medium: '-medium-',
        large: '-large-',
        original: '-original-'
    };
    
    const targetSize = sizeMap[size] || '-medium-';
    return baseUrl.replace(/-medium-/, targetSize);
};

const handleImageClick = () => {
    if (!props.clickable || !effectiveImageUrl.value) return;
    
    dialogImageUrl.value = effectiveImageUrl.value;
    isImageDialogVisible.value = true;
};

const handleImageLoad = () => {
    isLoading.value = false;
    hasError.value = false;
};

const handleImageError = () => {
    isLoading.value = false;
    hasError.value = true;
};

const triggerUpload = async () => {
    // Check if suggestions are supported for this asset type
    const assetType = props.data?.master_asset?.asset_type;
    if (ImageSuggestionService.isSupportedAssetType(assetType)) {
        await loadImageSuggestions();
        isSuggestionModalVisible.value = true;
    } else {
        // Fallback to direct file upload for unsupported asset types
        if (fileInput.value) {
            fileInput.value.click();
        }
    }
};

const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
        alert('Image file is too large. Please select a file smaller than 10MB');
        return;
    }
    
    await uploadImage(file);
};

const uploadImage = async (file) => {
    if (!props.data?.id) {
        console.error('No asset ID available for image upload');
        return;
    }
    
    try {
        isUploading.value = true;
        uploadProgress.value = 0;
        
        const uploaded = await LocalAssetService.uploadImage(props.data.id, file, {
            onUploadProgress: (progressEvent) => {
                uploadProgress.value = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
            }
        });
        
        // Update the data with the new image information
        if (uploaded) {
            Object.assign(props.data, uploaded);
            
            // Store validation data for display
            if (uploaded.validation) {
                imageValidation.value = uploaded.validation;
            }
            
            emit('image-updated', uploaded);
            emit('image-uploaded', uploaded);
        }
        
        // Reset file input
        if (fileInput.value) {
            fileInput.value.value = '';
        }
        
    } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
    } finally {
        isUploading.value = false;
        uploadProgress.value = 0;
    }
};

const confirmDelete = () => {
    isDeleteDialogVisible.value = true;
};

const deleteImage = async () => {
    if (!props.data?.id) {
        console.error('No asset ID available for image deletion');
        return;
    }
    
    try {
        isDeleting.value = true;
        
        const deleted = await LocalAssetService.deleteImage(props.data.id);
        
        // Update the data with the new image information
        if (deleted) {
            Object.assign(props.data, deleted);
            emit('image-updated', deleted);
            emit('image-deleted', deleted);
        }
        
        isDeleteDialogVisible.value = false;
        
    } catch (error) {
        console.error('Error deleting image:', error);
        alert('Failed to delete image. Please try again.');
    } finally {
        isDeleting.value = false;
    }
};

const viewSharedImage = () => {
    if (hasSubAssetImage.value) {
        dialogImageUrl.value = getImageSizeUrl(props.data.sub_asset_data.image_url, props.size);
        isImageDialogVisible.value = true;
    }
};

// Get all available image sizes for the dialog
const getAllImageSizes = () => {
    if (!props.data) return {};
    
    const baseUrl = props.data.image_url;
    
    if (props.data.image_sizes) {
        // Use the image_sizes data from upload response
        return props.data.image_sizes;
    } else if (baseUrl) {
        // Generate URLs for different sizes
        const sizeNames = ['thumbnail', 'medium', 'large', 'original'];
        const sizes = {};
        sizeNames.forEach(size => {
            sizes[size] = {
                url: getImageSizeUrl(baseUrl, size),
                width: getSizeDimensions(size).width,
                height: getSizeDimensions(size).height
            };
        });
        return sizes;
    }
    
    return {};
};

// Get dimensions for different sizes
const getSizeDimensions = (size) => {
    const dimensions = {
        thumbnail: { width: 150, height: 150 },
        small: { width: 150, height: 150 },
        medium: { width: 400, height: 400 },
        large: { width: 800, height: 800 },
        original: { width: null, height: null }
    };
    return dimensions[size] || { width: 400, height: 400 };
};

// Image suggestion methods
const loadImageSuggestions = async () => {
    if (!props.data?.id) {
        console.error('No asset ID available for image suggestions');
        return;
    }
    
    try {
        isLoadingSuggestions.value = true;
        suggestions.value = [];
        
        // Track analytics event
        ImageSuggestionService.trackEvent('image_suggestions_modal_opened', {
            asset_id: props.data.id,
            asset_type: props.data?.master_asset?.asset_type
        });
        
        const response = await ImageSuggestionService.getSuggestions(props.data.id);
        suggestions.value = response.suggestions || [];
        
    } catch (error) {
        console.error('Error loading image suggestions:', error);
        suggestions.value = [];
    } finally {
        isLoadingSuggestions.value = false;
    }
};

const handleSuggestionModalClose = () => {
    isSuggestionModalVisible.value = false;
    suggestions.value = [];
};

const handleUseSuggestion = async (suggestion) => {
    try {
        // Track analytics event
        ImageSuggestionService.trackEvent('image_suggestion_selected', {
            asset_id: props.data.id,
            suggestion_id: suggestion.cable_id,
            similarity_score: suggestion.similarity_score
        });
        
        // Show loading state
        isLoading.value = true;
        
        // Apply the suggested image using the API endpoint
        console.log('[AssetImage] Applying suggested image:', {
            assetId: props.data.id,
            imageUrl: suggestion.image_url,
            sourceCableId: suggestion.cable_id
        });
        
        const response = await ImageSuggestionService.applySuggestedImage(
            props.data.id,
            suggestion.image_url,
            suggestion.cable_id
        );
        
        console.log('[AssetImage] API response received:', response);
        
        if (response.success) {
            // Update the data with the new image information from API response
            const imageData = {
                image_url: response.image_url,
                image_source: response.image_source || 'local_asset',
                suggestion_data: suggestion
            };
            
            // Update the data with the new image information
            Object.assign(props.data, imageData);
            
            emit('image-updated', imageData);
            
            // Track usage event
            ImageSuggestionService.trackEvent('image_suggestion_used', {
                asset_id: props.data.id,
                suggestion_id: suggestion.cable_id,
                image_url: suggestion.image_url
            });
            
            // Close the modal
            isSuggestionModalVisible.value = false;
            suggestions.value = [];
        } else {
            throw new Error(response.message || 'Failed to apply suggested image');
        }
        
    } catch (error) {
        console.error('Error using suggestion:', error);
        alert(`Failed to apply suggested image: ${error.message || 'Please try again.'}`);
    } finally {
        isLoading.value = false;
    }
};

const handleUploadNew = () => {
    // Close the suggestion modal and open file picker
    isSuggestionModalVisible.value = false;
    suggestions.value = [];
    
    if (fileInput.value) {
        fileInput.value.click();
    }
};

// Watch for data changes
watch(() => props.data, (newData) => {
    if (newData) {
        // Only show loading spinner if there's actually an image URL
        // Otherwise, show the "No Image" icon immediately
        const baseUrl = newData.image_url;
        isLoading.value = !!baseUrl;
        hasError.value = false;
    }
}, { immediate: true });
</script>

<style scoped>
.asset-image-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.asset-image {
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Size variants */
.asset-image.size-thumbnail {
    width: 100px;
    height: 100px;
}

.asset-image.size-small {
    width: 150px;
    height: 150px;
}

.asset-image.size-medium {
    width: 200px;
    height: 200px;
}

.asset-image.size-large {
    width: 500px;
    height: 500px;
}

.asset-image.size-original {
    width: 600px;
    height: 600px;
}

.asset-image:hover {
    border-color: #3b82f6;
    transform: scale(1.05);
}

.asset-image.loading {
    border-color: #fbbf24;
}

.asset-image.error {
    border-color: #ef4444;
}

.asset-image.no-image {
    border-color: #d1d5db;
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.asset-image.no-image:hover {
    border-color: #9ca3af;
    background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
}

.asset-image-source-badge {
    position: absolute;
    top: 5px;
    right: 5px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
}

.company-image-badge {
    background: #10b981;
}

.shared-image-badge {
    background: #6b7280;
}

.no-image-badge {
    background: #ef4444;
}

.upload-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px;
    font-size: 10px;
    text-align: center;
}

.upload-progress-bar {
    height: 2px;
    background: #3b82f6;
    transition: width 0.3s ease;
}

.image-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.image-dialog-content {
    text-align: center;
}

.delete-confirmation {
    text-align: center;
}

.hidden {
    display: none;
}

.image-recommendations {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    max-width: 200px;
    z-index: 10;
}

.recommendation-header {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
}

.recommendation-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.recommendation-item {
    font-size: 11px;
    color: #6b7280;
    line-height: 1.3;
    margin-bottom: 2px;
}

.recommendation-item:last-child {
    margin-bottom: 0;
}
</style>
